import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { CheckCircle, Circle, Lock, Loader2, FileText, Award, Play, BookOpen, Clock, AlertCircle } from 'lucide-react';

const formatTime = (seconds) => {
  if (!seconds) return '0 min';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

// helpers color inline via CSS variables
const a1bg = (op = '15%') => `color-mix(in srgb, var(--org-accent,  #00FFFF) ${op}, transparent)`;
const a1bd = (op = '30%') => `color-mix(in srgb, var(--org-accent,  #00FFFF) ${op}, transparent)`;
const a2bg = (op = '15%') => `color-mix(in srgb, var(--org-accent2, #FF00FF) ${op}, transparent)`;
const a2bd = (op = '30%') => `color-mix(in srgb, var(--org-accent2, #FF00FF) ${op}, transparent)`;
const a3bg = (op = '15%') => `color-mix(in srgb, var(--org-accent3, #ccff00) ${op}, transparent)`;
const a3bd = (op = '30%') => `color-mix(in srgb, var(--org-accent3, #ccff00) ${op}, transparent)`;

const ModuleUnitsPanel = ({ moduleId, learningStyle = 'visual' }) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [units, setUnits]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [timeData, setTimeData]   = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [timeError, setTimeError] = useState(false);

  useEffect(() => { load(); }, [moduleId]);

  const load = async () => {
    try {
      const [unitsRes, moduleRes] = await Promise.all([
        api.get(`/modules/${moduleId}/submodules`),
        api.get(`/modules/${moduleId}`).catch(() => ({ data: null })),
      ]);
      setUnits(unitsRes.data || []);
      setModuleInfo(moduleRes.data);

      const reqTime = moduleRes.data?.required_time_seconds;
      if (reqTime && reqTime > 0) {
        try {
          const timeRes = await api.get(`/modules/${moduleId}/my-time`);
          setTimeData(timeRes.data);
          setTimeError(false);
        } catch {
          setTimeData({ total_seconds: 0, required_seconds: reqTime, percentage: 0, unlocked: false });
          setTimeError(true);
        }
      } else {
        setTimeData({ total_seconds: 0, required_seconds: 0, percentage: 100, unlocked: true });
      }
    } catch {
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--org-accent, #00FFFF)', filter: 'drop-shadow(0 0 10px var(--org-accent, #00FFFF))' }} />
      <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--org-accent, #00FFFF)' }}>Sincronizando Unidades...</span>
    </div>
  );

  if (units.length === 0) return null;

  const totalUnits  = units.length;
  const contentDone = units.filter(u =>
  (u.user_progress?.sections_completed?.length || 0) > 0 || u.user_progress?.test_passed
).length;
  const testsPassed = units.filter(u => u.user_progress?.test_passed).length;
  const overallPct  = totalUnits > 0 ? Math.round((contentDone / totalUnits) * 100) : 0;

  const requiredSecs = timeData?.required_seconds ?? 0;
  const showTime     = requiredSecs > 0;
  const timeUnlocked = !showTime || (timeData?.unlocked ?? true);

  return (
    <div className="space-y-6">

      {/* Summary card */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl shadow-[0_0_30px_rgba(0,0,0,0.5)] p-6 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-black text-[11px] uppercase tracking-widest flex items-center gap-2" style={{ color: 'var(--org-accent, #00FFFF)' }}>
            <BookOpen className="w-4 h-4" /> Progreso Estructural
          </h3>
          <span className="text-xl font-black" style={{ color: 'var(--org-accent3, #ccff00)', filter: 'drop-shadow(0 0 8px var(--org-accent3, #ccff00))' }}>{overallPct}%</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-6 border border-white/5">
          <div className="h-full rounded-full transition-all duration-700"
            style={{ width: `${overallPct}%`, background: 'var(--org-accent3, #ccff00)', boxShadow: '0 0 10px var(--org-accent3, #ccff00)' }} />
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="font-black text-white text-2xl mb-1">{totalUnits}</p>
            <p className="text-[9px] uppercase tracking-widest font-bold text-white/40">Unidades</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: a1bg('5%'), border: `1px solid ${a1bd('20%')}` }}>
            <p className="font-black text-2xl mb-1" style={{ color: 'var(--org-accent, #00FFFF)' }}>{contentDone}</p>
            <p className="text-[9px] uppercase tracking-widest font-bold mb-0" style={{ color: 'color-mix(in srgb, var(--org-accent, #00FFFF) 60%, transparent)' }}>Leídas</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: a3bg('5%'), border: `1px solid ${a3bd('20%')}` }}>
            <p className="font-black text-2xl mb-1" style={{ color: 'var(--org-accent3, #ccff00)' }}>{testsPassed}</p>
            <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: 'color-mix(in srgb, var(--org-accent3, #ccff00) 60%, transparent)' }}>Tests ✓</p>
          </div>
        </div>
      </div>

      {/* Tiempo mínimo */}
      {showTime && timeData && (
        <div className="rounded-2xl border p-5 backdrop-blur-md"
          style={timeUnlocked
            ? { background: a3bg('10%'), border: `1px solid ${a3bd('30%')}`, boxShadow: `inset 0 0 20px ${a3bg('10%')}` }
            : { background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', boxShadow: 'inset 0 0 20px rgba(249,115,22,0.1)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="p-2 rounded-lg border"
                style={timeUnlocked ? { background: a3bg('20%'), borderColor: a3bd('50%'), color: 'var(--org-accent3, #84cc16)' } : { background: 'rgba(249,115,22,0.2)', borderColor: 'rgba(249,115,22,0.5)', color: '#fb923c' }}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <span className="font-black text-white text-sm tracking-wide block">Tiempo Mínimo de Inmersión</span>
                <div className="flex items-center gap-2 mt-1">
                  {!timeUnlocked && (
                    <span className="text-[9px] bg-orange-500/20 text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded uppercase tracking-widest font-bold">
                      Evaluaciones Bloqueadas
                    </span>
                  )}
                  {timeUnlocked && (
                    <span className="text-[9px] px-2 py-0.5 rounded uppercase tracking-widest font-bold"
                      style={{ background: a3bg('20%'), color: 'var(--org-accent3, #84cc16)', border: `1px solid ${a3bd('40%')}` }}>
                      ✓ Sistema Desbloqueado
                    </span>
                  )}
                  {timeError && (
                    <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-white/40">
                      <AlertCircle className="w-3 h-3" /> Error de sincronización
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className="text-sm font-black tracking-wider text-white bg-black/40 px-3 py-1.5 rounded-lg border border-white/10 sm:ml-auto">
              {formatTime(timeData.total_seconds)} <span className="text-white/40 font-bold">/ {formatTime(timeData.required_seconds)}</span>
            </span>
          </div>
          <div className="h-2.5 bg-black/50 rounded-full overflow-hidden border border-white/5">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(timeData.percentage ?? 0, 100)}%`, background: timeUnlocked ? 'var(--org-accent3, #84cc16)' : '#f97316', boxShadow: timeUnlocked ? '0 0 10px var(--org-accent3, #84cc16)' : '0 0 10px #f97316' }} />
          </div>
          {!timeUnlocked && (
            <p className="text-[11px] text-orange-400/80 mt-3 font-bold uppercase tracking-widest">
              Requiere <strong className="text-orange-400">{formatTime(Math.max(0, timeData.required_seconds - timeData.total_seconds))}</strong> adicionales para autorizar tests.
            </p>
          )}
        </div>
      )}

      {/* Lista de unidades */}
      <div className="bg-black/20 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden border border-white/10">
        <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center gap-3">
          <BookOpen className="w-4 h-4" style={{ color: 'var(--org-accent2, #FF00FF)' }} />
          <h3 className="font-black text-[11px] uppercase tracking-widest" style={{ color: 'var(--org-accent2, #FF00FF)' }}>Sub-Unidades de Formación</h3>
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-white/40 bg-black/40 px-2 py-1 rounded-md border border-white/5">
            {testsPassed}/{totalUnits} Aprobadas
          </span>
        </div>

        <div className="divide-y divide-white/5">
          {units.map((unit, idx) => {
            const prog       = unit.user_progress || {};
            const tPassed    = !!prog.test_passed;
            const completed  = (prog.sections_completed?.length || 0) > 0 || tPassed;
            const tAttempted = !!prog.test_attempted;
            const prevUnit   = idx > 0 ? units[idx - 1] : null;
            const prevDone   = !prevUnit || !!prevUnit.user_progress?.test_passed;
            const isLocked   = !prevDone;
            const testLocked = !completed || !timeUnlocked;

            const handleClick = () => { if (!isLocked) navigate(`/module/${moduleId}/unit/${unit.id}`); };

            // Estilos del badge numérico
            const badgeStyle = tPassed
              ? { background: a3bg('10%'), borderColor: a3bd('40%'), color: 'var(--org-accent3, #ccff00)' }
              : completed
              ? { background: a1bg('10%'), borderColor: a1bd('40%'), color: 'var(--org-accent, #00FFFF)' }
              : isLocked
              ? { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.2)' }
              : { background: a2bg('10%'), borderColor: a2bd('40%'), color: 'var(--org-accent2, #FF00FF)' };

            return (
              <button
  type="button"
  key={unit.id}
  onClick={handleClick}
  disabled={isLocked}
  className={`w-full text-left flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-5 transition-all ${isLocked ? 'opacity-50 grayscale cursor-not-allowed bg-black/20' : 'cursor-pointer hover:bg-white/[0.03]'}`}
  style={!isLocked ? { borderLeft: '2px solid transparent' } : {}}
  onMouseEnter={e => { if (!isLocked) e.currentTarget.style.borderLeftColor = 'var(--org-accent, #00FFFF)'; }}
  onMouseLeave={e => { if (!isLocked) e.currentTarget.style.borderLeftColor = 'transparent'; }}
>
                {/* Badge numérico */}
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg border" style={badgeStyle}>
                  {tPassed ? <CheckCircle className="w-6 h-6" /> : isLocked ? <Lock className="w-5 h-5" /> : idx + 1}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-[15px] truncate mb-1.5">{unit.title}</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Estado contenido */}
                    <span className="flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest px-2 py-0.5 rounded-md border"
                      style={completed
                        ? { background: a1bg('10%'), color: 'var(--org-accent, #00FFFF)', borderColor: a1bd('20%') }
                        : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.1)' }}>
                      {completed ? <CheckCircle className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                      {completed ? 'Teoría Confirmada' : 'Teoría Pendiente'}
                    </span>

                    {/* Estado test */}
                    {unit.test_questions > 0 && (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest px-2 py-0.5 rounded-md border"
                        style={tPassed
                          ? { background: a3bg('10%'), color: 'var(--org-accent3, #ccff00)', borderColor: a3bd('20%') }
                          : testLocked
                          ? { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.1)' }
                          : tAttempted
                          ? { background: 'rgba(249,115,22,0.1)', color: '#fb923c', borderColor: 'rgba(249,115,22,0.2)' }
                          : { background: a2bg('10%'), color: 'var(--org-accent2, #FF00FF)', borderColor: a2bd('20%') }}>
                        {tPassed ? <Award className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                        {tPassed ? 'Test Aprobado'
                          : !completed ? 'Test Bloqueado'
                          : !timeUnlocked ? `⏱ Req: ${formatTime(Math.max(0, (timeData?.required_seconds ?? 0) - (timeData?.total_seconds ?? 0)))}`
                          : tAttempted ? 'Reintentar Test'
                          : 'Test Disponible'}
                      </span>
                    )}
                  </div>
                </div>

                {/* CTA */}
                {!isLocked && (
                  <div className="flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all"
                    style={tPassed
                      ? { background: a3bg('10%'), color: 'var(--org-accent3, #ccff00)', border: `1px solid ${a3bd('30%')}` }
                      : completed && unit.test_questions > 0 && !tPassed && timeUnlocked
                      ? { background: 'var(--org-accent2, #FF00FF)', color: '#fff', boxShadow: `0 0 15px ${a2bg('50%')}` }
                      : { background: 'var(--org-accent, #00FFFF)', color: '#000', boxShadow: `0 0 15px ${a1bg('50%')}` }}>
                    {tPassed ? (
                      <><CheckCircle className="w-4 h-4" /> Finalizada</>
                    ) : completed && unit.test_questions > 0 && !tPassed && timeUnlocked ? (
                      <><FileText className="w-4 h-4" /> {tAttempted ? 'Reintentar' : 'Evaluar'}</>
                    ) : (
                      <><Play className="w-4 h-4" /> {completed ? 'Repasar' : 'Acceder'}</>
                    )}
                  </div>
                )} 
                </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModuleUnitsPanel;